"use client";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../ui/button";

/* ------------------------------------------------------------------ */
/* Settings you can change                                             */
/* ------------------------------------------------------------------ */
const WORD = "AXIONBOTS"; // one letter per block; sorted order reads this word
const START = [4, 3, 8, 9, 6, 5, 1, 7, 2]; // starting heights, a permutation of 1..WORD.length
const HERO_SRC = `${process.env.PUBLIC_URL || ""}/media/hero-hanging.png`;

const NS = "http://www.w3.org/2000/svg";
const N = WORD.length;
const SLOT = 42, X0 = 11, BASE = 282, BW = 32, UNIT = 11, BASEH = 14;
const PENTA = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24];
const CHORDS = [[-3, 0, 4, 9], [-7, -4, 0, 5], [0, 4, 7, 12], [-5, -1, 2, 7]];

export default function PlayGame() {
  const rootRef = useRef(null);
  const playRef = useRef(null);
  const [label, setLabel] = useState("Play");
  const [busyBtn, setBusyBtn] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const q = (k) => root.querySelector(`[data-g="${k}"]`);
    const scene = q("scene"), blocksG = q("blocks"), websG = q("webs");
    const heroEl = q("hero"), flipEl = q("flip"), threadEl = q("thread");
    let dead = false;

    /* ---------------- sound (synthesised, no files) ---------------- */
    let AC = null, master = null, conv = null, musicTimer = null, beat = 0;
    let inView = false;
    const note = (step, base) => (base || 261.63) * Math.pow(2, step / 12);
    function ensureAudio() {
      if (!AC) {
        try {
          AC = new (window.AudioContext || window.webkitAudioContext)();
          const comp = AC.createDynamicsCompressor();
          master = AC.createGain(); master.gain.value = 0.9;
          master.connect(comp); comp.connect(AC.destination);
          const len = Math.floor(AC.sampleRate * 1.4), ir = AC.createBuffer(2, len, AC.sampleRate);
          for (let c = 0; c < 2; c++) {
            const d = ir.getChannelData(c);
            for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
          }
          conv = AC.createConvolver(); conv.buffer = ir;
          const wet = AC.createGain(); wet.gain.value = 0.28;
          conv.connect(wet); wet.connect(master);
        } catch (e) { AC = null; }
      }
      if (AC && AC.state === "suspended") AC.resume();
      if (AC && !musicTimer) musicTimer = setInterval(musicStep, 420);
    }
    function out(node, send) {
      node.connect(master);
      if (send && conv) { const g = AC.createGain(); g.gain.value = send; node.connect(g); g.connect(conv); }
    }
    function tone(f, d, type, vol, when, to, send) {
      if (!AC) return;
      const t = AC.currentTime + (when || 0), o = AC.createOscillator(), g = AC.createGain();
      o.type = type || "sine"; o.frequency.setValueAtTime(f, t);
      if (to) o.frequency.exponentialRampToValueAtTime(to, t + d);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(vol || 0.08, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      o.connect(g); out(g, send == null ? 0.35 : send); o.start(t); o.stop(t + d + 0.05);
    }
    function noise(d, f0, f1, vol, qv, when, type) {
      if (!AC) return;
      const len = Math.floor(AC.sampleRate * d), buf = AC.createBuffer(1, len, AC.sampleRate), data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      const src = AC.createBufferSource(), flt = AC.createBiquadFilter(), g = AC.createGain(), t = AC.currentTime + (when || 0);
      src.buffer = buf; flt.type = type || "bandpass"; flt.Q.value = qv || 4;
      flt.frequency.setValueAtTime(f0, t); flt.frequency.exponentialRampToValueAtTime(f1, t + d);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(vol, t + d * 0.2);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      src.connect(flt); flt.connect(g); out(g, 0.25); src.start(t);
    }
    const sfx = {
      start() { [0, 4, 7, 12].forEach((s, i) => tone(note(s, 392), 0.18, "square", 0.035, i * 0.07)); },
      pick() { tone(note(12), 0.12, "triangle", 0.09, 0, note(19)); tone(note(24), 0.08, "sine", 0.04, 0.03); },
      hover(slot) { tone(note(PENTA[slot % PENTA.length], 392), 0.11, "triangle", 0.06); },
      cancel() { tone(note(7), 0.16, "sine", 0.07, 0, note(-5)); },
      cmp(i) { tone(note(PENTA[i % PENTA.length], 392), 0.09, "triangle", 0.05); },
      thwip() { noise(0.24, 500, 5000, 0.42, 5); tone(420, 0.2, "sawtooth", 0.025, 0, 1500, 0.2); },
      whoosh() { noise(0.5, 300, 1800, 0.12, 1.2, 0.08); },
      thud() { tone(150, 0.16, "sine", 0.2, 0, 50, 0.1); noise(0.05, 2400, 900, 0.1, 1, 0, "highpass"); tone(880, 0.07, "triangle", 0.05, 0.02); },
      ok(k) { const s = PENTA[k % PENTA.length]; tone(note(s, 523.25), 0.5, "sine", 0.09, 0, null, 0.5); tone(note(s + 12, 523.25), 0.3, "triangle", 0.03, 0.01); },
      chime() {
        [0, 4, 7, 12, 16, 19, 24].forEach((s, i) => { tone(note(s, 523.25), 0.7, "sine", 0.1, i * 0.09, null, 0.55); tone(note(s, 523.25) * 2, 0.4, "triangle", 0.03, i * 0.09); });
        [0, 7, 12].forEach((s) => tone(note(s, 130.81), 1.4, "triangle", 0.07, 0.55, null, 0.3));
      },
    };
    // soft background music, only while this section is on screen
    function musicStep() {
      if (!AC || !inView || document.hidden) return;
      const ch = CHORDS[Math.floor(beat / 8) % CHORDS.length], i = beat % 8;
      const pat = [0, 1, 2, 3, 2, 1, 3, 2];
      tone(note(ch[pat[i]], 261.63), 0.9, "sine", 0.028, 0, null, 0.6);
      if (i === 0) tone(note(ch[0] - 12, 261.63), 2.6, "triangle", 0.035, 0, null, 0.3);
      if (i % 4 === 2) tone(note(ch[3] + 12, 261.63), 0.5, "triangle", 0.012, 0, null, 0.6);
      beat++;
    }
    const io = new IntersectionObserver((es) => { inView = es[0].isIntersecting; }, { threshold: 0.25 });
    io.observe(root);

    /* ---------------- scene ---------------- */
    const slotX = (i) => X0 + i * SLOT + (SLOT - BW) / 2;
    let blocks = [];
    function webOverlay(h) {
      const cx = BW / 2, cy = -h / 2;
      let d = `M0 ${-h} L${BW} 0 M${BW} ${-h} L0 0 M${cx} ${-h} L${cx} 0 M0 ${cy} L${BW} ${cy}`;
      for (let k = 1; k <= 4; k++) {
        const r = k * 4;
        d += ` M${cx - r} ${cy} Q${cx} ${cy - r * 0.55} ${cx + r} ${cy}`;
        d += ` M${cx} ${cy - r * 1.4} Q${cx + r * 0.5} ${cy} ${cx} ${cy + r * 1.4}`;
      }
      for (let y = 1; y < 6; y++) d += ` M-1 ${(-h * y) / 6} L${BW + 1} ${(-h * y) / 6 + (y % 2 ? 3 : -3)}`;
      return `<g class="axg-web" fill="none" stroke="#fff" stroke-width="0.9" stroke-linecap="round"><path d="${d}"/></g>`;
    }
    function makeBlock(v) {
      const g = document.createElementNS(NS, "g"); g.setAttribute("class", "axg-b");
      const h = BASEH + v * UNIT;
      g.innerHTML =
        `<rect class="axg-front" x="0" y="${-h}" width="${BW}" height="${h}" rx="2"/>` +
        `<path class="axg-top" d="M0 ${-h} L5 ${-h - 7} L${BW + 5} ${-h - 7} L${BW} ${-h} Z"/>` +
        `<text x="${BW / 2}" y="${-h / 2 + 6}">${WORD.charAt(v - 1)}</text>` + webOverlay(h);
      blocksG.appendChild(g);
      return { v, g, x: 0, lift: 0, h };
    }
    const draw = (b) => b.g.setAttribute("transform", `translate(${b.x},${BASE - b.lift})`);
    function build(arr) {
      blocksG.innerHTML = ""; blocks = arr.map(makeBlock);
      blocks.forEach((b, i) => { b.x = slotX(i); draw(b); });
    }
    const mark = (b, cls) => { if (b) b.g.setAttribute("class", "axg-b" + (cls ? " " + cls : "")); };

    /* ---------------- hero ---------------- */
    const hero = { x: 200, y: 36, tx: 200, ty: 36, vx: 0, cheer: 0, cheerT: 0, webs: null, webCol: "#8fd0ff", thread: 1 };
    const webLines = [0, 1].map(() => {
      const l = document.createElementNS(NS, "line"); l.setAttribute("stroke-linecap", "round"); websG.appendChild(l); return l;
    });
    const setLine = (el, x1, y1, x2, y2) => { el.setAttribute("x1", x1); el.setAttribute("y1", y1); el.setAttribute("x2", x2); el.setAttribute("y2", y2); };
    const t0 = performance.now();
    let raf = 0;
    function frame(now) {
      if (dead) return;
      const t = (now - t0) / 1000, px = hero.x;
      hero.x += (hero.tx - hero.x) * 0.075;
      hero.y += (hero.ty - hero.y) * 0.06;
      hero.vx = hero.x - px;
      hero.cheer += (hero.cheerT - hero.cheer) * 0.12;
      const bob = Math.sin(t * 2.2) * 1.6 * (1 - hero.cheerT);
      const hx = hero.x, hy = hero.y + bob, dx = hx - 200, L = Math.max(hy, 1);
      const tilt = hero.thread ? -Math.atan2(dx, L) : 0;
      const deg = (tilt * 180) / Math.PI + hero.vx * 5 * hero.thread;
      heroEl.setAttribute("transform", `translate(${hx},${hy}) rotate(${deg})`);
      setLine(threadEl, 200, -10, hx, hy); threadEl.style.opacity = hero.thread;
      flipEl.setAttribute("transform", `rotate(${180 * hero.cheer} 0 52)`);
      const cosA = Math.cos((deg * Math.PI) / 180), sinA = Math.sin((deg * Math.PI) / 180);
      const toScene = (lx, ly) => [hx + lx * cosA - ly * sinA, hy + lx * sinA + ly * cosA];
      const hands = [toScene(-3, 36), toScene(3, 36)];
      webLines.forEach((l, k) => {
        const b = hero.webs && blocks[hero.webs[k]];
        if (!b) { l.setAttribute("opacity", 0); return; }
        setLine(l, hands[k][0], hands[k][1], b.x + BW / 2 + 2, BASE - b.lift - b.h - 4);
        l.setAttribute("stroke", hero.webCol); l.setAttribute("stroke-width", 1.4); l.setAttribute("opacity", 0.9);
      });
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    /* ---------------- bubble sort steps ---------------- */
    function makeSteps(arr) {
      const a = arr.slice(), steps = [], n = a.length;
      for (let p = 0; p < n - 1; p++) {
        steps.push({ t: "pass", p });
        let swapped = false;
        for (let i = 0; i < n - 1 - p; i++) {
          steps.push({ t: "cmp", i });
          if (a[i] > a[i + 1]) { const tmp = a[i]; a[i] = a[i + 1]; a[i + 1] = tmp; swapped = true; steps.push({ t: "swap", i }); }
        }
        steps.push({ t: "end", idx: n - 1 - p, swapped });
        if (!swapped) break;
      }
      steps.push({ t: "done" });
      return steps;
    }

    /* ---------------- engine ---------------- */
    let gen = 0, steps = [], si = 0, current = START.slice();
    let running = false, busy = false, finished = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    function tween(ms, fn) {
      return new Promise((res) => {
        const st = performance.now(), my = gen;
        const tick = (now) => {
          if (my !== gen || dead) return res();
          const k = Math.min(1, (now - st) / ms);
          fn(k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2, k);
          if (k < 1) requestAnimationFrame(tick); else res();
        };
        tick(st);
      });
    }
    const centerOf = (i, j) => (slotX(i) + slotX(j) + BW) / 2;

    async function swapAnim(i, j, my) {
      const A = blocks[i], B = blocks[j];
      mark(A, "axg-swap"); mark(B, "axg-swap");
      hero.webs = [i, j]; hero.webCol = "#ffc766"; hero.tx = centerOf(i, j);
      sfx.thwip(); sfx.whoosh();
      const ax = A.x, bx = B.x;
      await tween(750, (e) => {
        A.x = ax + (bx - ax) * e; B.x = bx + (ax - bx) * e;
        A.lift = Math.sin(e * Math.PI) * 26; B.lift = 0;
        draw(A); draw(B); hero.tx = (A.x + B.x + BW) / 2;
      });
      if (my !== gen || dead) return;
      blocks[i] = B; blocks[j] = A;
      sfx.thud();
      await sleep(180);
      mark(A, ""); mark(B, ""); hero.webs = null;
    }
    const isSorted = () => blocks.every((b, i) => b.v === i + 1);
    function celebrate() {
      blocks.forEach((b) => mark(b, "axg-ok"));
      hero.webs = null; hero.thread = 0; hero.tx = 200; hero.cheerT = 1;
      hero.ty = BASE - blocks[Math.floor(N / 2)].h - 7 - 104;
      finished = true; sfx.chime();
    }
    async function exec(s, my) {
      if (s.t === "pass") {
        hero.webs = null; hero.ty = 36; await sleep(300);
      } else if (s.t === "cmp") {
        const A = blocks[s.i], B = blocks[s.i + 1];
        mark(A, "axg-cmp"); mark(B, "axg-cmp"); sfx.cmp(s.i);
        hero.tx = centerOf(s.i, s.i + 1); hero.webs = [s.i, s.i + 1]; hero.webCol = "#8fd0ff";
        await sleep(650);
        const nxt = steps[si + 1];
        if (!(nxt && nxt.t === "swap")) { mark(A, ""); mark(B, ""); hero.webs = null; }
      } else if (s.t === "swap") {
        await swapAnim(s.i, s.i + 1, my);
      } else if (s.t === "end") {
        hero.webs = null;
        if (blocks[s.idx]) { mark(blocks[s.idx], "axg-ok"); sfx.ok(s.idx); }
        if (!s.swapped) blocks.forEach((b) => mark(b, "axg-ok"));
        await sleep(400);
      } else if (s.t === "done") {
        celebrate(); await sleep(900);
      }
    }
    async function run() {
      const my = ++gen;
      steps = makeSteps(current); si = 0; finished = false;
      build(current); hero.thread = 1; hero.cheerT = 0; hero.tx = 200; hero.ty = 36; hero.webs = null;
      await sleep(450);
      while (my === gen && !dead && si < steps.length) {
        await exec(steps[si], my);
        if (my !== gen || dead) return;
        si++;
      }
      running = false; setBusyBtn(false); setLabel("Play again");
    }
    function shuffled() {
      const a = []; for (let k = 1; k <= N; k++) a.push(k);
      do { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } }
      while (a.every((v, i) => v === i + 1));
      return a;
    }

    playRef.current = () => {
      if (running || busy) return;
      ensureAudio(); sfx.start();
      current = finished ? shuffled() : blocks.map((b) => b.v);
      running = true; setBusyBtn(true); setLabel("Playing…");
      run();
    };

    /* ---------------- your move: drag a block onto another ---------------- */
    let drag = null;
    const sceneX = (e) => { const r = scene.getBoundingClientRect(); return ((e.clientX - r.left) * 400) / r.width; };
    const slotAt = (x) => Math.max(0, Math.min(N - 1, Math.floor((x - X0) / SLOT)));
    const onDown = (e) => {
      if (running || busy || finished) return;
      const g = e.target.closest && e.target.closest(".axg-b"); if (!g) return;
      const i = blocks.findIndex((b) => b.g === g); if (i < 0) return;
      ensureAudio(); drag = { from: i, hover: i };
      mark(blocks[i], "axg-cmp"); sfx.pick();
      try { scene.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      e.preventDefault();
    };
    const onMove = (e) => {
      if (!drag) return;
      const h = slotAt(sceneX(e));
      if (h !== drag.hover) {
        if (drag.hover !== drag.from) mark(blocks[drag.hover], "");
        drag.hover = h;
        if (h !== drag.from) { mark(blocks[h], "axg-swap"); sfx.hover(h); }
      }
    };
    async function release(cancel) {
      if (!drag) return;
      const d = drag; drag = null;
      mark(blocks[d.from], ""); mark(blocks[d.hover], "");
      if (cancel || d.hover === d.from) { if (cancel || d.hover !== d.from) sfx.cancel(); return; }
      busy = true;
      await swapAnim(Math.min(d.from, d.hover), Math.max(d.from, d.hover), gen);
      busy = false;
      if (!dead && isSorted()) { celebrate(); setLabel("Play again"); }
    }
    const onUp = () => release(false);
    const onCancel = () => release(true);
    const onTouchMove = (e) => { if (drag) e.preventDefault(); };
    scene.addEventListener("pointerdown", onDown);
    scene.addEventListener("pointermove", onMove);
    scene.addEventListener("pointerup", onUp);
    scene.addEventListener("pointercancel", onCancel);
    scene.addEventListener("touchmove", onTouchMove, { passive: false });

    build(current);

    return () => {
      dead = true; gen++;
      cancelAnimationFrame(raf);
      clearInterval(musicTimer);
      io.disconnect();
      scene.removeEventListener("pointerdown", onDown);
      scene.removeEventListener("pointermove", onMove);
      scene.removeEventListener("pointerup", onUp);
      scene.removeEventListener("pointercancel", onCancel);
      scene.removeEventListener("touchmove", onTouchMove);
      if (AC) { try { AC.close(); } catch (e) { /* ignore */ } }
    };
  }, []);

  return (
    <section id="play" className="relative py-24 md:py-32" data-testid="play-section">
      <style>{`
        .axg-b { cursor: grab; }
        .axg-b .axg-front { fill: #243247; } .axg-b .axg-top { fill: #34475f; }
        .axg-b.axg-cmp .axg-front { fill: #1b9be0; } .axg-b.axg-cmp .axg-top { fill: #6fdcff; }
        .axg-b.axg-swap .axg-front { fill: #e58d12; } .axg-b.axg-swap .axg-top { fill: #ffc766; }
        .axg-b.axg-ok .axg-front { fill: #2563eb; } .axg-b.axg-ok .axg-top { fill: #6ea0ff; }
        .axg-b .axg-web { opacity: 0; transition: opacity .25s; pointer-events: none; }
        .axg-b.axg-cmp .axg-web { opacity: .55; } .axg-b.axg-swap .axg-web { opacity: 1; }
        .axg-b text { font: 800 17px Outfit, system-ui, sans-serif; fill: #fff; text-anchor: middle; pointer-events: none; }
        .axg-scene { touch-action: pan-y; user-select: none; -webkit-user-select: none; }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#00E5FF] mb-5" data-testid="play-label">
            Play
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight" data-testid="play-heading">
            Sort the word{" "}
            <span className="gradient-text font-medium">{WORD}</span>
          </h2>
          <p className="mt-5 text-neutral-400 text-base md:text-lg">
            Drag one block onto another and our hero swaps them with his web.
            Or press Play and watch bubble sort do it.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8 }}
          className="relative mt-12 mx-auto w-full max-w-md rounded-3xl border-gradient glow-soft overflow-hidden bg-[#050505]"
          data-testid="play-game-card"
        >
          <div ref={rootRef} className="relative p-5 pb-8 bg-gradient-to-b from-[#0d0709] to-[#050505]">
            <svg
              data-g="scene"
              className="axg-scene block w-full h-auto overflow-visible"
              viewBox="0 0 400 330"
              role="img"
              aria-label="A hero hangs from a thread and swaps letter blocks until they spell AXIONBOTS"
              data-testid="play-scene"
            >
              <defs>
                <linearGradient id="axgPlat" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#3b4a63" />
                  <stop offset="1" stopColor="#1c2535" />
                </linearGradient>
              </defs>
              <ellipse cx="200" cy="318" rx="170" ry="8" fill="#000" opacity=".45" />
              <path d="M6 296 L26 280 L374 280 L394 296 L394 312 L6 312 Z" fill="url(#axgPlat)" />
              <rect x="6" y="296" width="388" height="2" fill="#5a6c88" opacity=".6" />
              <g data-g="blocks" />
              <g data-g="webs" />
              <line data-g="thread" stroke="#fff" strokeWidth="1.6" opacity=".9" />
              <g data-g="hero">
                <g data-g="flip">
                  <image href={HERO_SRC} x="-39" y="-5" width="78" height="114" />
                </g>
              </g>
            </svg>

            <div className="mt-5 flex justify-center">
              <Button
                type="button"
                disabled={busyBtn}
                onClick={() => playRef.current && playRef.current()}
                className="bg-[#229ED9] hover:bg-[#1A8BC2] text-white rounded-full h-12 px-9 glow-blue disabled:opacity-70"
                data-testid="play-button"
              >
                {label}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
